import React, { createContext, useContext, useEffect, useState } from 'react';
import { onAuthStateChanged, signInWithEmailAndPassword, createUserWithEmailAndPassword, sendPasswordResetEmail, signOut as firebaseSignOut } from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import { auth, functions, db } from '../services/firebaseService';
import firebaseService from '../services/firebaseService';
import analytics from '../utils/analytics';
import toast from 'react-hot-toast';
// Removed: import { useNavigate } from 'react-router-dom'; // No longer needed here

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  // Removed: const navigate = useNavigate(); // No longer needed here

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const userProfile = await firebaseService.getUserByFirebaseUid(user.uid);
        const profileComplete = !!userProfile?.username;
        const fullUser = { ...user, ...userProfile, profileComplete };
        setCurrentUser(fullUser);
        
        // Track login when user is authenticated
        if (userProfile) {
          analytics.trackLogin({
            id: user.uid,
            email: user.email,
            username: userProfile.username,
            firstName: userProfile.firstName,
            lastName: userProfile.lastName,
            isAdmin: userProfile.isAdmin,
            createdAt: userProfile.createdAt
          });
        }
      } else {
        setCurrentUser(null);
      }
      setLoading(false);
    });

    return unsubscribe;
  }, []);

  // Removed: Effect to handle post-login redirection for deep links
  // This logic will be moved to a dedicated AuthRedirector component

  const login = async ({ identifier, password }) => {
    try {
      let emailToLogin = identifier;

      if (!identifier.includes('@')) {
        const userProfile = await firebaseService.getUserByUsername(identifier);
        if (!userProfile || !userProfile.email) {
          throw new Error('No user found with that username.');
        }
        emailToLogin = userProfile.email;
      }

      await signInWithEmailAndPassword(auth, emailToLogin, password);
    } catch (error) {
      console.error("Error logging in:", error);
      throw new Error(error.message || 'Failed to log in.');
    }
  };

  const signup = async (signupData) => {
    try {
      const referralCodeUsed = localStorage.getItem('referralCodeUsed');
      const userRegistrationFn = httpsCallable(functions, 'UserRegistration');
      const result = await userRegistrationFn({
        ...signupData,
        referralCodeUsed,
        source: 'PWA',
      });

      if (result.data && result.data.success) {
        // Track registration
        // analytics.trackRegistration({
        //   id: result.data.uid,
        //   email: signupData.email,
        //   username: signupData.username,
        //   firstName: signupData.firstName,
        //   lastName: signupData.lastName,
        //   isAdmin: false,
        //   createdAt: new Date().toISOString()
        // }, signupData.referralCodeUsed, 'PWA');

        try {
          await signInWithEmailAndPassword(auth, signupData.email, signupData.password);
        } catch (signInError) {
          console.error("Error signing in after signup:", signInError);
          if (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1') {
            throw new Error(`Account created, but auto-login failed: ${signInError.message}. Please ensure the Auth emulator is running.`);
          }
          throw signInError;
        }
      } else {
        throw new Error(result.data?.message || 'Failed to sign up.');
      }
    } catch (error) {
      console.error("Error signing up:", error);
      throw new Error(error.message || 'Failed to sign up.');
    }
  };

  const resetPassword = async (email) => {
    try {
      await sendPasswordResetEmail(auth, email);
    } catch (error) {
      console.error("Error sending password reset email:", error);
      throw new Error(error.message || 'Failed to send password reset email.');
    }
  };

  const signOut = async () => {
    try {
      await firebaseSignOut(auth);
      analytics.reset();
    } catch (error) {
      console.error("Error signing out:", error);
      throw new Error(error.message || 'Failed to log in.');
    }
  };

  const refreshUser = async () => {
    if (!auth.currentUser) return;
    
    try {
      const userProfile = await firebaseService.getUserByFirebaseUid(auth.currentUser.uid);
      const profileComplete = !!userProfile?.username;
      setCurrentUser({ ...auth.currentUser, ...userProfile, profileComplete });
    } catch (error) {
      console.error("Error refreshing user profile:", error);
    }
  };

  const acceptTOS = async () => {
    if (!auth.currentUser) return;
    
    try {
      await firebaseService.updateUserTOS(auth.currentUser.uid);
      await refreshUser();
      console.log("TOS accepted and updated in database.");
    } catch (error) {
      console.error("Error updating TOS acceptance:", error);
      throw error;
    }
  };

  const sendVerificationCode = async (identifier) => {
    console.error("sendVerificationCode not fully implemented in AuthContext. Requires client-side reCAPTCHA for phone auth or specific email link setup.");
    toast.error("Passwordless login is not currently available.");
    throw new Error("Passwordless login not implemented.");
  };

  const verifyCodeAndLogin = async (identifier, code) => {
    console.error("verifyCodeAndLogin not fully implemented in AuthContext. Requires client-side reCAPTCHA for phone auth or specific email link setup.");
    toast.error("Passwordless login is not currently available.");
    throw new Error("Passwordless login not implemented.");
  };

  const completeProfile = async (profileData) => {
    try {
      const referralCodeUsed = localStorage.getItem('referralCodeUsed');
      const userRegistrationFn = httpsCallable(functions, 'UserRegistration');
      const result = await userRegistrationFn({
        ...profileData,
        referralCodeUsed,
        uid: currentUser?.uid,
        isGoogleSignIn: true,
        source: 'PWA',
      });

      if (result.data && result.data.success) {
        // Track registration/completion for Google Sign-in users
        // analytics.trackRegistration({
        //   id: currentUser?.uid,
        //   email: currentUser?.email,
        //   username: profileData.username,
        //   firstName: profileData.firstName,
        //   lastName: profileData.lastName,
        //   isAdmin: currentUser?.isAdmin || false,
        //   createdAt: new Date().toISOString()
        // }, null, 'PWA');

        await refreshUser();
        return result.data;
      } else {
        throw new Error(result.data?.message || 'Failed to complete profile.');
      }
    } catch (error) {
      console.error("Error completing profile:", error);
      throw new Error(error.message || 'Failed to complete profile.');
    }
  };

  const value = {
    currentUser,
    loading,
    login,
    signup,
    completeProfile,
    resetPassword,
    signOut,
    refreshUser,
    acceptTOS,
    sendVerificationCode,
    verifyCodeAndLogin,
  };

  return (
    <AuthContext.Provider value={value}>
      {!loading && children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};